import React, { useEffect, useState } from 'react';
import { useWorldStore, ZOOM } from '../../state/worldStore';
import { HUD_MAX } from '../../config';
import { FEEL_CLASSIC } from '../walkBody';
import Radar, { RADAR_SIZE } from './Radar';
import ZoneCard from './ZoneCard';
import RadioWheel, { StationCard } from './RadioWheel';
import DriveHud from '../drive/DriveHud';
import { useDriveStore } from '../drive/driveStore';
import { usePovStore, TP_TEST_BODY } from '../player';
import { useHasBody } from '../../hooks/usePov';
import { isTyping } from '../../utils/typing';
import { blip } from '../sfx';
import { SLICE_DISTRICT } from '../../config';
import { api } from '../../services/api';
import { ROOMS } from '../../rooms';
import { overKeys } from '../../components/hudStack';

// ── THE GAME LAYER OF THE HUD ────────────────────────────────────────────────
//
// What turns a walkable scene into something that plays like an open-world
// game: a radar in the corner, the name of the place you just walked into, and
// a radio wheel under one key. Each is self-contained and positions itself in
// the same centred HUD column as <Hud/>, so on an ultrawide it all stays in one
// glance. Street only — rooms keep their own chrome.
//
// The radar is a desktop element: on touch the bottom-left belongs to the
// joystick, and the tap-to-travel town map already does the job there.

export const IS_TOUCH = typeof window !== 'undefined' && (('ontouchstart' in window) || navigator.maxTouchPoints > 0);

/** Is the radar on screen right now? (Hud moves the now-playing card over for it.) */
export function radarShown(zoom, entered) {
  return !FEEL_CLASSIC && !IS_TOUCH && entered && zoom === ZOOM.STREET;
}
export const RADAR_GUTTER = RADAR_SIZE + 14;

export default function GameHud() {
  const entered = useWorldStore((s) => s.entered);
  const zoom = useWorldStore((s) => s.zoom);
  const ride = useWorldStore((s) => s.ride);
  const [tuned, setTuned] = useState(null);
  const [hint, setHint] = useState(false);
  const driving = useDriveStore((s) => s.driving) != null;

  // V — first / third person (third only with a body to look at).
  const hasBody = useHasBody() || TP_TEST_BODY != null;
  const toggleView = usePovStore((s) => s.toggle);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    const down = (e) => {
      // On the street and in a walk-in room (2026-11-01: third person works
      // indoors now — PlanetScene InteriorWalk/RoomChase); not in an orbit room.
      const st = useWorldStore.getState();
      // Not in a look-only room (rooms.js `lookOnly`): always first person there.
      const here = st.zoom === ZOOM.STREET || (st.zoom === ZOOM.INTERIOR && !!ROOMS[st.interior]?.firstPerson && !ROOMS[st.interior]?.lookOnly);
      if (e.code !== 'KeyV' || e.repeat || isTyping(e) || !here) return;
      if (!hasBody) {
        // Third person needs a body: a mannequin, chosen on the selection
        // screen (game/mannequins) — the HUD's Body button reopens it.
        setToast({ text: 'Choose a body (Body, top left) to see yourself', at: Date.now() });
        return;
      }
      toggleView();
      blip(740, 0.5);
      setToast({ text: usePovStore.getState().wantThird ? 'Third person' : 'First person', at: Date.now() });
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, [hasBody, toggleView]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 1800);
    return () => clearTimeout(t);
  }, [toast]);

  // Getting into a car announces the station you are listening to, the way
  // the radio does when you start the engine.
  useEffect(() => {
    const on = () => {
      if (!useWorldStore.getState().musicOn) { setTuned({ name: 'Radio Off', at: Date.now() }); return; }
      api.stations().then((d) => {
        const s = (d?.stations || []).find((x) => x.slug === SLICE_DISTRICT.stationSlug);
        setTuned({ name: s?.name || SLICE_DISTRICT.name, host_name: s?.host_name, at: Date.now() });
      }).catch(() => {});
    };
    window.addEventListener('ht:station-card', on);
    return () => window.removeEventListener('ht:station-card', on);
  }, []);

  // One quiet line about the controls the first time someone is on the street.
  const arrivedAt = useWorldStore((s) => s.arrivedAt);
  useEffect(() => {
    if (!arrivedAt || IS_TOUCH || FEEL_CLASSIC) return undefined;
    let seen = false;
    try { seen = localStorage.getItem('world:gameHint') === '1'; } catch { /* private mode */ }
    if (seen) return undefined;
    const a = setTimeout(() => setHint(true), 5500);
    const b = setTimeout(() => { setHint(false); try { localStorage.setItem('world:gameHint', '1'); } catch { /* noop */ } }, 13500);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, [arrivedAt]);

  if (!entered || FEEL_CLASSIC) return null;
  const street = zoom === ZOOM.STREET;
  return (
    <div style={{
      position: 'fixed', top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)',
      width: '100%', maxWidth: HUD_MAX, pointerEvents: 'none', zIndex: 11,
    }}>
      {radarShown(zoom, entered) && !ride && (
        <div style={{ position: 'absolute', left: 'max(16px, env(safe-area-inset-left))', bottom: 'max(16px, env(safe-area-inset-bottom))' }}>
          <Radar />
        </div>
      )}
      {street && <ZoneCard touch={IS_TOUCH} />}
      {street && <DriveHud touch={IS_TOUCH} />}
      {street && <StationCard station={tuned} />}
      <RadioWheel onTuned={setTuned} />
      {/* Touch has no Q: the station wheel now opens from the PLAYER BAR (tap
          what is playing — components/PlayerBar), not a separate floating
          button (Khari, 2026-10-23: "clean up the radio, music, track title"). */}
      {false && IS_TOUCH && street && (
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('ht:radio-open'))}
          aria-label="Radio stations"
          style={{
            position: 'absolute', right: 'max(16px, env(safe-area-inset-right))', top: 'calc(max(16px, env(safe-area-inset-top)) + 108px)',
            pointerEvents: 'auto', width: 42, height: 42, borderRadius: 999, border: '1px solid rgba(255,178,94,0.35)',
            background: 'rgba(20,12,6,0.65)', color: '#ffd27d', font: '700 18px system-ui', display: 'grid', placeItems: 'center',
          }}
        >📻</button>
      )}
      {toast && (
        <div key={toast.at} style={{
          position: 'absolute', left: '50%', top: '42%', transform: 'translate(-50%, -50%)',
          background: 'rgba(10,12,16,0.72)', borderRadius: 999, padding: '8px 18px',
          color: '#fff', font: '700 13px system-ui', letterSpacing: 0.5, whiteSpace: 'nowrap',
        }}>{toast.text}</div>
      )}
      {hint && street && !driving && (
        <div style={{
          // Above the WASD keys (they publish their top, hudStack): a fixed +200px
          // sat on them once they rose over the quick-speak mic.
          position: 'absolute', left: '50%', bottom: overKeys(), transform: 'translateX(-50%)',
          background: 'rgba(10,12,16,0.72)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 999,
          padding: '8px 16px', color: 'rgba(255,255,255,0.88)', font: '600 12px system-ui', letterSpacing: 0.4,
          whiteSpace: 'nowrap', animation: 'htHint 8000ms ease forwards',
        }}>
          <style>{'@keyframes htHint { 0% { opacity: 0 } 8% { opacity: 1 } 88% { opacity: 1 } 100% { opacity: 0 } }'}</style>
          <b>Shift</b> sprint · <b>Space</b> jump · <b>F</b> take a car · hold <b>Q</b> radio{hasBody ? <> · <b>V</b> view</> : null}
        </div>
      )}
    </div>
  );
}
